import * as React from "react";
import styled from "@emotion/styled";
import { useState, useRef, useEffect } from "react";
import AudioPlayer, { RHAP_UI } from "react-h5-audio-player";
import { AudioButton, PauseButton, PlayButton } from "../../icons";
import { MiniProgress } from "./MiniProgress";
import { useMiniMode } from "./useMiniMode";
import "./audio_player.scss";

export interface AudioProps {
  src: string;

  // when set to false, the player will pause & contract
  active?: boolean;

  // when set to true, the player will always be expanded
  alwaysExpanded?: boolean;

  // when set to true, the player will never expand
  isMini?: boolean;

  // when set to true, the player will expand from right to left
  openLeft?: boolean;

  // callback that fires when audio starts playing
  onPlay?: () => void;

  // callback that fires when audio is paused
  onPause?: () => void;

  // callback that fires when audio finishes
  onEnded?: () => void;
}

export const MINI_PROGRESS_RADIUS = 24;

export const Audio: React.FC<AudioProps> = (props: AudioProps) => {
  const {
    src,
    active,
    alwaysExpanded,
    isMini,
    openLeft,
    onPlay,
    onPause,
    onEnded,
  } = props;

  const [initialFocus, setInitialFocus] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const audioPlayerRef = useRef<AudioPlayer | null>(null);
  const timerContainer = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const { circleRef, onPlayMini, onPauseMini, onEndMini } = useMiniMode({
    radius: MINI_PROGRESS_RADIUS,
  });

  const getCurrentTimeAndDuration = () => {
    const duration = audioPlayerRef.current?.audio.current?.duration || 0;
    const currentTime = audioPlayerRef.current?.audio.current?.currentTime || 0;

    return { currentTime, duration };
  };

  const openPlayer = () => {
    setInitialFocus(true);
    if (!isMini) {
      setExpanded(true);
    }
  };

  /**
   * move built in timer components to custom container on mount
   */
  useEffect(() => {
    if (wrapperRef.current) {
      const currentTime =
        wrapperRef.current.querySelector(".rhap_current-time");
      const duration = wrapperRef.current.querySelector(".rhap_total-time");

      if (currentTime && duration && timerContainer.current) {
        timerContainer.current.prepend(currentTime);
        timerContainer.current.append(duration);
      }
    }
  }, []);

  /**
   * pause audio & contract when the player is inactive
   * i.e. when another player on the page is activated
   */
  useEffect(() => {
    if (audioPlayerRef.current) {
      if (!active) {
        audioPlayerRef.current.audio.current?.pause();
        setExpanded(false);
      }
    }
  }, [active]);

  const onAudioPlay = () => {
    if (isMini) {
      const { duration, currentTime } = getCurrentTimeAndDuration();
      onPlayMini(duration, currentTime);
    }
    if (onPlay) {
      onPlay();
    }
  };

  const onAudioPause = () => {
    if (isMini) {
      const { duration, currentTime } = getCurrentTimeAndDuration();
      onPauseMini(duration, currentTime);
    }
    if (onPause) {
      onPause();
    }
  };

  const onAudioEnded = () => {
    setInitialFocus(false);
    if (isMini) {
      onEndMini();
    }
    if (onEnded) {
      onEnded();
    }
  };

  const expandedClassName = expanded || alwaysExpanded ? "expanded" : "";
  /* eslint-disable react/jsx-indent */
  const customProgressBarSection = isMini
    ? undefined
    : [
        RHAP_UI.CURRENT_TIME,
        RHAP_UI.DURATION,
        <div
          key="custom-timing"
          className={`custom-timing ${expandedClassName}`}
          ref={timerContainer}
        >
          <div className="time-divider"> / </div>
        </div>,
        RHAP_UI.PROGRESS_BAR,
      ];
  /* eslint-enable react/jsx-indent */

  return (
    <Wrapper>
      <AudioWrapper
        onClickCapture={openPlayer}
        ref={wrapperRef}
        openLeft={openLeft}
      >
        {isMini && (
          <MiniProgress radius={MINI_PROGRESS_RADIUS} circleRef={circleRef} />
        )}
        <AudioPlayer
          ref={audioPlayerRef}
          src={src}
          className={expandedClassName}
          showSkipControls={false}
          showJumpControls={false}
          layout="horizontal-reverse"
          customIcons={{
            play: initialFocus ? <PlayButton /> : <AudioButton />,
            pause: <PauseButton />,
          }}
          /**
           * the sections of the audio player -- volume, additional controls (looping), and progress bar are customizable below.
           * the library will map over the prop values to determine which components should render in those sections
           * an empty array indicates that we're not using that section
           * RHAP_UI values map to built-in components, but we can also add custom elements (like the custom-timing div below)
           * more info in the docs for react-h5-audio-player https://www.npmjs.com/package/react-h5-audio-player
           */

          customVolumeControls={[]}
          customAdditionalControls={[]}
          customProgressBarSection={customProgressBarSection}
          onPlay={onAudioPlay}
          onPause={onAudioPause}
          onEnded={onAudioEnded}
        />
      </AudioWrapper>
    </Wrapper>
  );
};

const AudioWrapper = styled.div((props: { openLeft?: boolean }) => ({
  position: "absolute",
  right: props.openLeft ? 0 : "unset",
}));

const Wrapper = styled.div({
  position: "relative",
});
